import { useEffect, useRef, useState } from 'react';

/**
 * PWAInstallPrompt
 * ─────────────────
 * A small circular floating "install" FAB that appears on the Home screen
 * whenever the app is being viewed in a browser and is NOT already installed.
 *
 * Visibility rule (viewport-fixed, no scroll drift):
 *   show  iff  !standalone && !installed
 *
 * Tap behaviour, in priority order:
 *   1. If we captured a `beforeinstallprompt` (Chromium desktop/Android) → native prompt
 *   2. iOS Safari                                        → 3-step Share → Add-to-Home instructions
 *   3. Any other browser (Firefox / desktop Safari / etc)→ generic browser-menu instructions
 *
 * The `beforeinstallprompt` event is captured in `index.js` before React
 * mounts, then read here via `window.__pwaDeferredPrompt`. This prevents
 * the previous race where the event fired before the component mounted
 * and the FAB never became visible.
 */

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if (window.matchMedia?.('(display-mode: fullscreen)').matches) return true;
  if (window.matchMedia?.('(display-mode: minimal-ui)').matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
};

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
};

const isSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
};

export default function PWAInstallPrompt() {
  // Read the prompt captured before React mounted (see index.js).
  const [deferredPrompt, setDeferredPrompt] = useState(
    typeof window !== 'undefined' ? window.__pwaDeferredPrompt : null
  );
  const [standalone, setStandalone] = useState(isStandalone());
  const [installed, setInstalled] = useState(
    typeof window !== 'undefined' ? !!window.__pwaInstalled : false
  );
  const [open, setOpen] = useState(false);
  const cardRef = useRef(null);

  const iosSafari = isIOS() && isSafari();

  useEffect(() => {
    const onPromptReady = () => setDeferredPrompt(window.__pwaDeferredPrompt);
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); setOpen(false); };
    // Custom bridge events dispatched by the early-capture code in index.js.
    window.addEventListener('pwa:prompt-ready', onPromptReady);
    window.addEventListener('pwa:installed', onInstalled);
    // Also listen directly in case index.js's listener was never registered
    // (e.g. code split, older bundle in cache) — belt-and-braces.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.__pwaDeferredPrompt = e;
    };
    const onAppInstalled = () => onInstalled();
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);

    const mql = window.matchMedia?.('(display-mode: standalone)');
    const onModeChange = () => setStandalone(isStandalone());
    mql?.addEventListener?.('change', onModeChange);

    return () => {
      window.removeEventListener('pwa:prompt-ready', onPromptReady);
      window.removeEventListener('pwa:installed', onInstalled);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
      mql?.removeEventListener?.('change', onModeChange);
    };
  }, []);

  // Close card on Escape and outside-tap.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onClick = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const t = setTimeout(() => window.addEventListener('pointerdown', onClick), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onClick);
      clearTimeout(t);
    };
  }, [open]);

  // The ONLY hard hide conditions — everything else keeps the FAB visible so
  // the user always has a way to install, even on browsers that don't fire
  // `beforeinstallprompt` (Firefox, desktop Safari, some preview embeds).
  if (standalone || installed) return null;

  const handleFabTap = () => setOpen(v => !v);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') setInstalled(true);
      } catch { /* dismissed */ }
      setDeferredPrompt(null);
      window.__pwaDeferredPrompt = null;
      setOpen(false);
    }
  };

  return (
    <>
      {/* Floating circular install FAB — always visible on Home when not installed */}
      <button
        type="button"
        onClick={handleFabTap}
        aria-label="Install PCOS Insights"
        aria-expanded={open}
        data-testid="pwa-install-fab"
        style={{
          position: 'fixed',
          right: 'max(16px, env(safe-area-inset-right, 16px))',
          bottom: 'calc(64px + max(20px, env(safe-area-inset-bottom, 0px)))',
          width: 48,
          height: 48,
          borderRadius: '50%',
          border: 'none',
          background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)',
          color: '#fff',
          boxShadow: '0 6px 18px rgba(109,94,245,0.35), 0 1px 3px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 1200,
          transition: 'transform .2s ease, box-shadow .2s ease',
          animation: 'pwa-fab-enter .35s ease-out both',
          pointerEvents: 'auto',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3v12" />
          <polyline points="7 10 12 15 17 10" />
          <rect x="4" y="17" width="16" height="4" rx="1" />
        </svg>
      </button>

      {/* Compact install popover */}
      {open && (
        <div
          ref={cardRef}
          role="dialog"
          aria-label="Install PCOS Insights"
          data-testid="pwa-install-card"
          style={{
            position: 'fixed',
            right: 'max(12px, env(safe-area-inset-right, 12px))',
            bottom: 'calc(64px + 60px + max(20px, env(safe-area-inset-bottom, 0px)))',
            width: 'min(320px, calc(100vw - 24px))',
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 12px 40px rgba(60,50,120,0.18), 0 2px 6px rgba(0,0,0,0.06)',
            padding: '16px 16px 14px',
            zIndex: 1300,
            animation: 'pwa-card-enter .18s ease-out both',
            border: '1px solid rgba(167,139,250,0.15)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <img
              src="/app_icon_96.png"
              alt=""
              aria-hidden="true"
              width={36}
              height={36}
              style={{ borderRadius: 10, flexShrink: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#1F1B2E', lineHeight: 1.3, marginBottom: 4 }}>
                Install PCOS Insights
              </div>
              <div style={{ fontSize: 13, color: '#6B6478', lineHeight: 1.45 }}>
                Add PCOS Insights to your home screen for quicker access.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close install prompt"
              data-testid="pwa-install-close"
              style={{
                width: 28, height: 28, borderRadius: 8, border: 'none',
                background: 'transparent', color: '#8A847C',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0, marginRight: -6, marginTop: -4,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Action row: three exclusive branches */}
          {deferredPrompt ? (
            <button
              type="button"
              onClick={handleInstall}
              data-testid="pwa-install-btn"
              style={{
                marginTop: 14, width: '100%', height: 40, borderRadius: 10,
                border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)',
                color: '#fff', fontSize: 14, fontWeight: 600,
                boxShadow: '0 4px 12px rgba(109,94,245,0.25)',
              }}
            >
              Install
            </button>
          ) : iosSafari ? (
            <ol style={{
              marginTop: 12, marginBottom: 0, paddingLeft: 22, color: '#4A4358',
              fontSize: 12.5, lineHeight: 1.55,
            }} data-testid="pwa-install-ios-steps">
              <li>Tap the <strong>Share</strong> button in Safari.</li>
              <li>Select <strong>Add to Home Screen</strong>.</li>
              <li>Tap <strong>Add</strong>.</li>
            </ol>
          ) : (
            <div style={{
              marginTop: 12, color: '#4A4358', fontSize: 12.5, lineHeight: 1.55,
            }} data-testid="pwa-install-manual-steps">
              Open your browser menu and choose{' '}
              <strong>Install app</strong> or <strong>Add to Home screen</strong>.
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes pwa-fab-enter {
          0%   { opacity: 0; transform: scale(0.85); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes pwa-card-enter {
          0%   { opacity: 0; transform: translateY(4px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </>
  );
}
